import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChevronRight, Search } from 'lucide-react'

import { ActivityDetailPanel } from '@/admin/components/communications/activity-detail-panel'
import {
  ActivityChannelPill,
  ActivitySkipReason,
  ActivityStatusPill,
} from '@/admin/components/communications/activity-pills'
import { DataTable } from '@/admin/components/data-table/data-table'
import type { DataTableColumn } from '@/admin/components/data-table/data-table'
import {
  useCommunicationsActivity,
  useCommunicationsActivitySummary,
} from '@/admin/hooks/use-communications-activity'
import type {
  ActivityChannel,
  ActivityRow,
  ActivityStatus,
} from '@/admin/hooks/use-communications-activity'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'

/**
 * Settings -> Messaging -> Activity.
 *
 * Every message the platform sent on the tenant's behalf, and every one it did
 * not. Chrome follows /settings/webhooks (stat tiles, tabs with counts, teaching
 * empty state) and the table follows /settings/api-logs, so this reads as part
 * of the same family rather than a new idiom.
 */

const STATUS_OPTIONS: ActivityStatus[] = [
  'delivered',
  'opened',
  'clicked',
  'bounced',
  'failed',
  'skipped',
]

type TabKey = 'messages' | 'voice'

/**
 * Landing scope is today, per the locked decision that "sent today" is the
 * question the console opens on. Widening is one select away.
 */
type PeriodKey = 'today' | '7d' | '30d' | 'all'

/** Default page: enough to fill the settings pane without turning the table into
 * an endless scroll. Adjustable from the table footer. */
const DEFAULT_PAGE_SIZE = 25

const PERIOD_DAYS: Record<PeriodKey, number | null> = {
  today: 0,
  '7d': 7,
  '30d': 30,
  all: null,
}

/** ISO lower bound for a period, or undefined for all time. */
function periodStart(period: PeriodKey): string | undefined {
  const days = PERIOD_DAYS[period]
  if (days == null) return undefined
  const start = new Date()
  if (days === 0) start.setHours(0, 0, 0, 0)
  else start.setDate(start.getDate() - days)
  return start.toISOString()
}

/** "31 Jul 00:26" - narrow enough for the settings pane; full value on hover. */
function compactTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/**
 * Prefix per resource type. Maps a backend-supplied type to a display token -
 * the backend owns which types exist, this only spells them.
 */
const RECORD_PREFIXES: Record<string, string> = {
  claim: 'CLM',
  holding: 'REG',
  inspection: 'INS',
  transfer: 'TRF',
  lab_task: 'LAB',
  lab_sample: 'SMP',
}

/** A readable reference for the record a message relates to. */
function recordReference(row: ActivityRow): string {
  if (!row.target_uuid || !row.target_type) return ''
  const short = row.target_uuid.slice(0, 8).toUpperCase()
  const prefix = RECORD_PREFIXES[row.target_type]
  // Unknown type: show the id rather than nothing, so a newly addressable
  // record is still traceable before it gets a prefix here.
  return prefix ? `${prefix}-${short}` : short
}

/**
 * Wording for a server-supplied kind. The backend owns which kinds exist; this
 * only translates one, and falls back to the raw value so a newly added kind
 * still renders something readable rather than nothing.
 */
function kindLabel(t: TFunction, value: string): string {
  return t(`comms.kind.${value}`, {
    defaultValue: value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
  })
}

function StatTile({
  label,
  value,
  sub,
  tone,
  testId,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'warn'
  testId?: string
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-[8px] bg-[var(--bg-subtle)] px-[18px] py-4"
    >
      <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </p>
      <p
        className={
          tone === 'warn'
            ? 'font-display text-[28px] leading-none text-[var(--status-warn)]'
            : 'font-display text-[28px] leading-none text-[var(--ink)]'
        }
      >
        {value}
      </p>
      {sub && <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{sub}</p>}
    </div>
  )
}

export function CommunicationsActivityPage() {
  const { t } = useTranslation()
  const { formatDateTime } = useFormatters()

  const smsEnabled = useFeatureFlag(FLAGS.SMS)
  const voiceEnabled = useFeatureFlag(FLAGS.VOICE)

  const navigate = useNavigate()
  // ?channel= seeds the initial filter, so the retired /settings/sms-activity
  // path can redirect here without losing its SMS scope, and so a channel view
  // is linkable. A channel the tenant does not have is ignored rather than
  // applied: the select that would clear it is hidden, and the backend
  // intersects with the enabled set, so it would strand the user on an empty
  // table.
  const [searchParams] = useSearchParams()
  const requestedChannel = searchParams.get('channel') ?? ''
  // Mirrors the channel select's own options: email is unconditional, sms is
  // flag-gated. WhatsApp has no frontend flag yet, so it is not offered here.
  const initialChannel =
    requestedChannel === 'email' || (requestedChannel === 'sms' && smsEnabled)
      ? requestedChannel
      : ''
  const [expanded, setExpanded] = useState<string | null>(null)
  const [tab, setTab] = useState<TabKey>('messages')
  const [search, setSearch] = useState('')
  const [channel, setChannel] = useState<string>(initialChannel)
  const [status, setStatus] = useState<string>('')
  const [kind, setKind] = useState<string>('')
  const [period, setPeriod] = useState<PeriodKey>('today')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Memoised: periodStart() reads the clock, so recomputing it inline gave every
  // render a new query key and refetched in a loop.
  const dateFrom = useMemo(() => periodStart(period), [period])

  const params = useMemo(
    () => ({
      source: tab === 'voice' ? (['voice'] as const) : (['message'] as const),
      channel: channel ? ([channel] as ActivityChannel[]) : undefined,
      status: status ? ([status] as ActivityStatus[]) : undefined,
      kind: kind ? [kind] : undefined,
      date_from: dateFrom,
      search: search.trim() || undefined,
      page,
      page_size: pageSize,
    }),
    [tab, channel, status, kind, dateFrom, search, page, pageSize],
  )

  // Any filter change re-scopes the result set, so holding page 5 would land on
  // a page that no longer exists.
  useEffect(() => {
    setPage(1)
  }, [tab, channel, status, kind, period, search])

  const {
    data,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useCommunicationsActivity({ ...params, source: [...params.source] })
  // Same period as the table: tiles that counted a different range than the
  // rows below them would just be confusing.
  const { data: summary } = useCommunicationsActivitySummary({
    date_from: dateFrom,
  })

  const rows = data?.results ?? []
  const isFiltered = Boolean(channel || status || kind || search)

  const notSent = summary?.by_status?.skipped ?? 0
  const bounced = summary?.by_status?.bounced ?? 0
  const failed = summary?.by_status?.failed ?? 0
  const total = summary?.total ?? 0

  const availableKinds = summary?.available_kinds ?? []

  // Deliverability is only meaningful once the provider has actually reported
  // something. Without tracking every row sits at "sent" forever, and reading
  // that as 100% delivered was the overstatement this page exists to prevent.
  //
  // Once events do arrive, "delivered" has to be inferred from the absence of a
  // failure: Mandrill emits send / bounce / open / click and has no delivered
  // event at all, so counting only delivered+opened+clicked would understate a
  // healthy send by several times over.
  const opened = summary?.by_status?.opened ?? 0
  const clicked = summary?.by_status?.clicked ?? 0
  const deliveredReported = summary?.by_status?.delivered ?? 0
  const hasDeliveryFeedback =
    deliveredReported + opened + clicked + bounced + failed > 0
  const notFailed = Math.max(0, total - bounced - failed)
  const engaged = opened + clicked

  const channelBreakdown = Object.entries(summary?.by_channel ?? {})
    .map(([key, count]) => `${count} ${key}`)
    .join(' · ')

  const rowId = (row: ActivityRow) => `${row.source}:${row.uuid}`

  const columns: DataTableColumn<ActivityRow>[] = [
    {
      id: 'occurred_at',
      header: t('comms.activity.time', { defaultValue: 'Time' }),
      cell: (row) => (
        // Compact: the settings content pane is ~815px, and a full
        // "Jul 31, 2026, 12:26 AM" spent 191px of it. Full timestamp on hover.
        <span
          title={formatDateTime(row.occurred_at)}
          className="whitespace-nowrap font-mono text-[12px] tabular-nums text-[var(--ink-2)]"
        >
          {compactTime(row.occurred_at)}
        </span>
      ),
    },
    {
      id: 'channel',
      header: t('comms.activity.channel', { defaultValue: 'Channel' }),
      cell: (row) => <ActivityChannelPill channel={row.channel} />,
    },
    {
      id: 'counterparty',
      header: t('comms.activity.recipient', { defaultValue: 'Recipient' }),
      cell: (row) => (
        <div className="flex min-w-0 max-w-[150px] flex-col gap-0.5">
          {row.counterparty_name && (
            <span className="truncate text-[12.6px] text-[var(--ink)]">
              {row.counterparty_name}
            </span>
          )}
          <span className="truncate font-mono text-[10.4px] text-[var(--ink-3)]">
            {row.counterparty || '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'summary',
      header:
        tab === 'voice'
          ? t('comms.activity.call', { defaultValue: 'Call' })
          : t('comms.activity.message', { defaultValue: 'Message' }),
      // Fixed width rather than max-w-0: with content-sized siblings the
      // browser hands the slack to them and a max-w-0 column collapses to
      // nothing. The record reference lives here rather than in its own column
      // because six columns do not fit the ~815px settings pane with a readable
      // message, and the reference reads better next to what it refers to.
      cell: (row) => {
        const reference = recordReference(row)
        return (
          <div className="flex w-[210px] min-w-0 flex-col gap-0.5">
            <span
              title={row.summary}
              className="truncate text-[12.6px] text-[var(--ink-2)]"
            >
              {row.summary || '-'}
            </span>
            {reference && (
              <span className="truncate font-mono text-[10.4px] text-[var(--ink-3)]">
                {reference}
              </span>
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      header:
        tab === 'voice'
          ? t('comms.activity.direction', { defaultValue: 'Direction' })
          : t('comms.activity.status', { defaultValue: 'Status' }),
      cell: (row) =>
        tab === 'voice' ? (
          <span className="font-mono text-[10.5px] uppercase text-[var(--ink-2)]">
            {row.direction}
          </span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <ActivityStatusPill status={row.status} />
            {row.skip_reason_label && (
              <ActivitySkipReason label={row.skip_reason_label} />
            )}
          </div>
        ),
    },
    {
      id: 'disclosure',
      header: '',
      cell: (row) => (
        <ChevronRight
          size={13}
          aria-hidden
          className={cn(
            'text-[var(--ink-3)] transition-transform',
            expanded === rowId(row) && 'rotate-90',
          )}
        />
      ),
    },
  ]

  return (
    <div className="mt-8">
      <div className="mb-1.5 flex items-start gap-4">
        <h2 className="font-display m-0 whitespace-nowrap text-[26px] font-medium leading-none tracking-[-0.01em] text-[var(--ink)]">
          {t('comms.activity.title', { defaultValue: 'Communications' })}
        </h2>
      </div>
      <p className="mb-[18px] max-w-[74ch] text-[12.6px] text-[var(--ink-2)]">
        {t('comms.activity.lead', {
          defaultValue:
            'Every message the platform sent on your behalf, and every one it did not. Search by customer or record to see exactly what was delivered.',
        })}
      </p>

      <div className="mb-6 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <StatTile
          label={
            period === 'today'
              ? t('comms.activity.tileSentToday', { defaultValue: 'Sent today' })
              : t('comms.activity.tileSent', { defaultValue: 'Messages' })
          }
          value={String(total)}
          sub={channelBreakdown || undefined}
          testId="comms-activity-tile-total"
        />
        <StatTile
          testId="comms-activity-tile-delivered"
          label={t('comms.activity.tileDelivered', { defaultValue: 'Delivered' })}
          value={
            total > 0 && hasDeliveryFeedback
              ? `${Math.round((notFailed / total) * 100)}%`
              : '-'
          }
          sub={
            hasDeliveryFeedback
              ? t('comms.activity.tileDeliveredSub', {
                  defaultValue: '{{bounced}} bounced - {{failed}} failed',
                  bounced,
                  failed,
                })
              : t('comms.activity.tileNoFeedback', {
                  defaultValue: 'no delivery reports from the email provider yet',
                })
          }
        />
        <StatTile
          testId="comms-activity-tile-engagement"
          label={t('comms.activity.tileEngagement', { defaultValue: 'Engagement' })}
          value={
            total > 0 && engaged > 0 ? `${Math.round((engaged / total) * 100)}%` : '-'
          }
          sub={
            engaged > 0
              ? t('comms.activity.tileEngagementSub', {
                  defaultValue: 'opened or clicked through',
                })
              : t('comms.activity.tileNoTracking', {
                  defaultValue: 'open and click tracking not reporting yet',
                })
          }
        />
        <StatTile
          testId="comms-activity-tile-not-sent"
          label={t('comms.activity.tileNotSent', { defaultValue: 'Not sent' })}
          value={String(notSent)}
          tone={notSent > 0 ? 'warn' : undefined}
          sub={t('comms.activity.tileNotSentSub', {
            defaultValue: 'skipped, with a reason',
          })}
        />
      </div>

      {voiceEnabled && (
        <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
          <TabsList variant="line">
            <TabsTrigger value="messages">
              {t('comms.activity.tabMessages', { defaultValue: 'Messages' })}
            </TabsTrigger>
            <TabsTrigger value="voice">
              {t('comms.activity.tabVoice', { defaultValue: 'Voice' })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="relative mb-2.5 mt-4">
        <Search
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('comms.activity.searchPlaceholder', {
            defaultValue: 'Search by customer name, email, phone, or record reference...',
          })}
          className="pl-9"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {tab === 'messages' && smsEnabled && (
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger
              data-testid="comms-activity-filter-channel"
              className="w-auto min-w-[132px]"
            >
              <SelectValue
                placeholder={t('comms.activity.allChannels', {
                  defaultValue: 'All channels',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">
                {t('comms.activity.allChannels', { defaultValue: 'All channels' })}
              </SelectItem>
              <SelectItem value="email">
                {t('comms.channel.email', { defaultValue: 'Email' })}
              </SelectItem>
              <SelectItem value="sms">
                {t('comms.channel.sms', { defaultValue: 'SMS' })}
              </SelectItem>
            </SelectContent>
          </Select>
        )}
        {tab === 'messages' && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              data-testid="comms-activity-filter-status"
              className="w-auto min-w-[132px]"
            >
              <SelectValue
                placeholder={t('comms.activity.allStatuses', {
                  defaultValue: 'All statuses',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">
                {t('comms.activity.allStatuses', { defaultValue: 'All statuses' })}
              </SelectItem>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`comms.status.${option}`, { defaultValue: option })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {tab === 'messages' && availableKinds.length > 0 && (
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger
              data-testid="comms-activity-filter-kind"
              className="w-auto min-w-[124px]"
            >
              <SelectValue
                placeholder={t('comms.activity.allKinds', { defaultValue: 'All kinds' })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">
                {t('comms.activity.allKinds', { defaultValue: 'All kinds' })}
              </SelectItem>
              {availableKinds.map((value) => (
                <SelectItem key={value} value={value}>
                  {kindLabel(t, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={period} onValueChange={(value) => setPeriod(value as PeriodKey)}>
          <SelectTrigger
            data-testid="comms-activity-filter-period"
            className="w-auto min-w-[112px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">
              {t('comms.activity.periodToday', { defaultValue: 'Today' })}
            </SelectItem>
            <SelectItem value="7d">
              {t('comms.activity.period7d', { defaultValue: 'Last 7 days' })}
            </SelectItem>
            <SelectItem value="30d">
              {t('comms.activity.period30d', { defaultValue: 'Last 30 days' })}
            </SelectItem>
            <SelectItem value="all">
              {t('comms.activity.periodAll', { defaultValue: 'All time' })}
            </SelectItem>
          </SelectContent>
        </Select>
        {(channel || status || kind || search || period !== 'today') && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              setChannel('')
              setStatus('')
              setKind('')
              setPeriod('today')
              setSearch('')
            }}
          >
            {t('comms.activity.clearFilters', { defaultValue: 'Clear filters' })}
          </Button>
        )}
      </div>

      <DataTable<ActivityRow>
        tableId="communications-activity"
        // Scroll inside a fixed frame rather than growing the page: raising the
        // page size should not push the pager below the fold.
        className="max-h-[560px] overflow-y-auto"
        columns={columns}
        data={rows}
        // Both flags: isLoading alone would flash the empty state when a cached
        // empty page refetches (CLAUDE.md empty-state rule).
        isLoading={isPending}
        isFetching={isFetching}
        isError={isError}
        onRetry={refetch}
        rowKey={rowId}
        rowTestId={(row) => `comms-activity-row-${row.uuid}`}
        page={page}
        pageSize={pageSize}
        // Undefined when the backend cannot count exactly, which hides the
        // footer rather than showing a wrong page total.
        totalCount={data?.count ?? undefined}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        onRowClick={(row) =>
          setExpanded((current) => (current === rowId(row) ? null : rowId(row)))
        }
        renderRowDetail={(row) =>
          expanded === rowId(row) ? (
            <ActivityDetailPanel
              row={row}
              onOpenRecord={(targetType, targetUuid) => {
                if (targetType === 'claim') navigate(`/claims/${targetUuid}`)
                else if (targetType === 'holding') navigate(`/registrations/${targetUuid}`)
              }}
              onOpenTemplate={() => navigate('/settings/email-templates')}
            />
          ) : null
        }
        emptyStateNode={
          // A narrowed view must not claim the tenant has never sent anything -
          // the teaching copy is only honest on an unfiltered landing.
          isFiltered ? (
            <div className="px-8 py-12 text-center">
              <p className="text-[12.6px] text-[var(--ink-3)]">
                {t('comms.activity.emptyFiltered', {
                  defaultValue: 'No messages match this view.',
                })}
              </p>
            </div>
          ) : (
            <div className="px-8 py-12 text-center">
              <h3 className="font-display m-0 mb-2.5 text-[22px] leading-tight text-[var(--ink)]">
                {period === 'today'
                  ? t('comms.activity.emptyToday', {
                      defaultValue: 'Nothing sent today',
                    })
                  : t('comms.activity.emptyTitle', {
                      defaultValue: 'No messages yet',
                    })}
              </h3>
              <p className="mx-auto max-w-[52ch] text-[12.6px] leading-relaxed text-[var(--ink-3)]">
                {t('comms.activity.emptyBody', {
                  defaultValue:
                    'When a registration is confirmed or a claim changes stage, the emails and texts that go out appear here, along with anything that was skipped and the reason why.',
                })}
              </p>
            </div>
          )
        }
      />
    </div>
  )
}

export default CommunicationsActivityPage
